import {
	ReactElement,
	Ref,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { useActiveSettingsMenuItem } from "../../SidebarMenu/sidebarMenuSelectionStore";
import { SettingsPage } from "../../SettingsPage";
import { localize } from "client_legacy/ndaw/culture";
import {
	computeContextMenuTitle,
	ContextMenu,
	FloatingActionButton,
	Pagination,
	SearchInputForm,
	SearchPlaceholder,
	TableView,
	TableViewNoResultConfig,
	ToolbarAction,
	ToolbarRegularAction,
	useConfirmationDialog,
	useContextMenu
} from "client/Components";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import {
	AlertManager,
	createLocalDataSource,
	MediaQueryKind,
	useAlerts,
	useForceRerender,
	useSelection,
	useSortHandler,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	EnvironmentTopDeskConnectionListItemModel,
	TopDeskConnectionListItemModel,
	deleteMultiple,
	list
} from "client/Api/TopDeskConfiguration/topDeskConfiguration";
import { contains } from "client/Data/searching";
import { DataSourceSortDirection, DataSourceSortableState } from "client/Data/sorting";
import {
	ActionKind,
	buildActionsForTopBar,
	deselectAllTitle
} from "client_legacy/ndaw/actions";
import { EditTopDeskConnectionDialog } from "./EditTopDeskConnectionDialog";
import { resolveFormattedTextString } from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export const TopDeskConnectionPage = (): ReactElement => {
	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [items, setitems] = useState<readonly TopDeskConnectionListItemModel[]>([]);
	const [selectedItems, setSelectedItems] = useState<readonly EnvironmentTopDeskConnectionListItemModel[]>([]);
	const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
	const [searchTerm, setSearchTerm] = useState<string>("");

	const [configureDialogState, setConfigureDialogState] = useState<ConfigureConnectionState>({
		isVisible: false
	});

	const [sortingState, setSortingState] = useState<DataSourceSortableState<TopDeskConnectionListItemModel>>(
		{ propertyName: "name", direction: DataSourceSortDirection.Ascending }
	);

	const bodyRef: Ref<HTMLDivElement> = useRef<HTMLDivElement>(null);

	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const alertManager: AlertManager = useAlerts();
	useActiveSettingsMenuItem(SettingsPageKind.TopDesk);

	const [contextMenuHandler, contextMenuProps] = useContextMenu(bodyRef);
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const [reload, loadVersion] = useForceRerender();
	const { sortingFor } = useSortHandler<TopDeskConnectionListItemModel>(sortingState, setSortingState);

	const { currentItems, pagination } = useDataSource(
		items,
		{
			sorting: sortingState,
			pagination: {
				currentPageNumber,
				amountPerPage: 25
			},
			searchTerm
		}
	);

	const { multiSelection, selectionFor } = useSelection(
		currentItems.filter((x): x is EnvironmentTopDeskConnectionListItemModel => !x.isDefault),
		"id",
		{
			isMultiSelect: true,
			selectedItems,
			setSelectedItems,
			hasMemorization: false
		}
	);

	const newHandler = useCallback(
		(): void => {
			setConfigureDialogState({
				isVisible: true
			});
		},
		[]
	);

	const editHandler = useCallback(
		(item: EnvironmentTopDeskConnectionListItemModel): void => {
			setConfigureDialogState({
				isVisible: true,
				connectionId: item.id
			});
		},
		[]
	);

	const deleteHandler = useCallback(
		async (items: readonly EnvironmentTopDeskConnectionListItemModel[]): Promise<void> => {
			const ids: readonly number[] = items.map(x => x.id);

			const result: TryResult<void> = await openConfirmationDialog({
				trackingId: "delete-topdesk-connection-confirmation-dialog",
				title: ids.length === 1
					? localize("_AreYouSureYouWantToDeleteThisConnection")
					: localize("_AreYouSureYouWantToDeleteTheseConnections"),
				confirmText: localize("_Delete")
			});

			if (!result.wasSuccessful) {
				return;
			}

			setIsBusy(true);

			try {
				await deleteMultiple(ids, globalCancellation);
				setitems((x) => x.filter(c => c.isDefault || !ids.includes(c.id)));
				setIsBusy(false);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_ErrorDeletingConnections"));
			}
		},
		[
			alertManager,
			globalCancellation,
			openConfirmationDialog
		]
	);

	useEffect(
		(): (() => void) => {
			hasImplicitDependencyOn(loadVersion);

			const load = async (signal: AbortSignal): Promise<void> => {
				setIsBusy(true);

				try {
					const connections: readonly TopDeskConnectionListItemModel[] = await list(signal)
						.catch(handlePageDataLoadingError);

					setitems(connections);
					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsBusy(false);
				}
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return () => cancellation.abort();
		},
		[alertManager, loadVersion]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(loadVersion);
			setCurrentPageNumber(1);
		},
		[loadVersion]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(
				currentPageNumber,
				sortingState,
				searchTerm
			);

			bodyRef.current
				?.scrollTo(0, 0);
		},
		[
			currentPageNumber,
			searchTerm,
			sortingState
		]
	);

	const actionContext = useMemo(
		(): TopDeskConnectionActionContext => ({
			selectedItems,
			isMobile
		}),
		[isMobile, selectedItems]
	);

	const selectionActions: readonly TopDeskConnectionAction[] = useMemo(
		() => getSelectionActions({
			deselectHandler: () => setSelectedItems([])
		}),
		[]
	);

	const contextualActions: readonly TopDeskConnectionAction[] = useMemo(
		() => listContextualActions({
			edit: editHandler,
			delete: deleteHandler
		}),
		[deleteHandler, editHandler]
	);

	const mainAction = useMemo(
		(): ToolbarRegularAction<TopDeskConnectionActionContext> => {
			return getMainAction(newHandler);
		},
		[newHandler]
	);

	const hasSelection: boolean = actionContext.selectedItems
		.length !== 0;

	const topBarActions = useMemo(
		(): readonly TopDeskConnectionAction[] => {
			return buildActionsForTopBar<TopDeskConnectionActionContext, TopDeskConnectionAction>(
				{
					mainActions: [mainAction],
					selectionActions,
					contextualActions
				},
				hasSelection,
				isResponsive,
				false
			);
		},
		[
			hasSelection,
			contextualActions,
			isResponsive,
			mainAction,
			selectionActions
		]
	);

	const noResultConfig = useMemo(
		(): TableViewNoResultConfig => {
			if (searchTerm) {
				return {
					icon: "magnifying-glass",
					title: localize("_NoSearchResults"),
					message: localize("_PleaseTrySearchingWithAnotherTerm")
				};
			}

			return {
				icon: "gear",
				title: resolveFormattedTextString({
					template: localize("_NoXIntegrationConnectionsYet"),
					replacements: [
						{
							what: "{{IntegrationName}}",
							filler: localize("_TopDesk")
						}
					]
				}),
				message: resolveFormattedTextString({
					template: localize("_ThereAreNoXIntegrationConnectionsYet"),
					replacements: [
						{
							what: "{{IntegrationName}}",
							filler: localize("_TopDesk")
						}
					]
				})
			};
		},
		[searchTerm]
	);

	return (
		<SettingsPage
			hasBusyIndicator={isBusy}
			actions={topBarActions}
			actionContext={actionContext}
			hasSelection={hasSelection}
			title={localize("_TopDeskConnections")}
		>
			<SettingsPage.Section
				height="stretch"
			>
				<SettingsPage.SearchSection>
					<SearchInputForm
						onChange={setSearchTerm}
						trackingId="topdesk-search-input"
						icon="filter"
						height="large"
						placeholder={(
							<SearchPlaceholder
								kind="filter"
								properties={[localize("_Name")]}
							/>
						)}
					/>
				</SettingsPage.SearchSection>

				<SettingsPage.Section height="stretch">
					<TableView
						multiSelectable={multiSelection}
						columns={[
							{
								trackingId: "name",
								title: localize("_Name"),
								sortable: sortingFor("name")
							},
							{
								trackingId: "url",
								title: localize("_TopDeskUrl")
							}
						]}
						ref={bodyRef}
						height="stretch"
						items={currentItems}
						itemKey={itemKey}
						trackingId="top-desk-connections"
						noResultConfig={noResultConfig}
					>
						{(item, itemKey) => (
							<TableView.Row
								trackingId={`${itemKey}`}
								selectable={item.isDefault
									? undefined
									: selectionFor(item)
								}
								onExecute={item.isDefault
									? undefined
									: () => editHandler(item)
								}
								onContextMenu={contextMenuHandler}
							>
								<TableView.Cell>{item.name}</TableView.Cell>
								<TableView.Cell>{item.serverUrl}</TableView.Cell>
							</TableView.Row>
						)}
					</TableView>
					<Pagination {...pagination} onPageNumberChanged={setCurrentPageNumber} />
					{contextMenuProps && (
						<ContextMenu
							{...contextMenuProps}
							actions={contextualActions}
							context={actionContext}
							trackingId="topdesk-connection-overview-context-menu"
							title={computeContextMenuTitle(
								actionContext.selectedItems,
								(x) => x.name
							)}
						/>
					)}
				</SettingsPage.Section>
			</SettingsPage.Section>
			{mainAction.canExecute(actionContext) && (
				<>
					<div className="preserve-space-for-mobile-plus-button"/>
					<FloatingActionButton
						onClick={() => mainAction.execute(actionContext)}
						icon="plus"
						trackingId="new-button"
					/>
				</>
			)}
			{confirmationDialog}

			{configureDialogState.isVisible && (
				<EditTopDeskConnectionDialog
					id={configureDialogState.connectionId}
					onSaved={reload}
					onClose={() => setConfigureDialogState({ isVisible: false })}
				/>
			)}
			<AiAssistantFabSpacing />
		</SettingsPage>
	);
};
TopDeskConnectionPage.displayName = "TopDeskConnectionPage";

type ConfigureConnectionState = {
	isVisible: boolean;
	connectionId?: number;
};

function searchMatchFunction(
	item: TopDeskConnectionListItemModel,
	normalizedNotEmptySearchTerm: Exclude<string, "">
): boolean {
	const { name } = item;

	return contains(name, normalizedNotEmptySearchTerm);
}

const useDataSource = createLocalDataSource<TopDeskConnectionListItemModel>()
	.setConfiguration({
		search: {
			doesMatchItem: searchMatchFunction,
		},
		hasPagination: true
	})
	.getHook();

type TopDeskConnectionActionContext = {
	selectedItems: readonly EnvironmentTopDeskConnectionListItemModel[];
	isMobile: boolean;
};

type TopDeskConnectionAction = ToolbarAction<TopDeskConnectionActionContext>;

type ContextualActionsHandlers = {
	edit(item: EnvironmentTopDeskConnectionListItemModel): void;
	delete(items: readonly EnvironmentTopDeskConnectionListItemModel[]): void;
};

function listContextualActions(
	handlers: ContextualActionsHandlers
): readonly TopDeskConnectionAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "edit",
			title: localize("_Edit"),
			canExecute: (c) => c.selectedItems.length === 1,
			execute: (c) => handlers.edit(c.selectedItems[0]),
			icon: "pen"
		},
		{
			kind: ActionKind.Regular,
			name: "delete",
			title: localize("_Delete"),
			canExecute: (c) => c.selectedItems.length > 0,
			execute: (c) => handlers.delete(c.selectedItems),
			icon: "trash-can"
		}
	];
}

type SelectionActionsHandlers = {
	deselectHandler: () => void;
};

function getSelectionActions(handlers: SelectionActionsHandlers): readonly TopDeskConnectionAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "deselectAll",
			title: (c) => deselectAllTitle(c.selectedItems),
			icon: "circle-xmark",
			canExecute: (c) => c.selectedItems.length > 0,
			execute: () => handlers.deselectHandler()
		},
		{
			kind: ActionKind.Divider,
			name: "deselect-all-divider",
			canExecute: (c) => !c.isMobile
				&& c.selectedItems.length > 0
		}
	];
}

function getMainAction(
	newHandler: () => void
): ToolbarRegularAction<TopDeskConnectionActionContext> {
	return {
		kind: ActionKind.Regular,
		name: "createNew",
		title: localize("_New"),
		canExecute: () => true,
		execute: () => newHandler(),
		icon: "plus",
		isPrimary: true
	};
}

function itemKey(item: TopDeskConnectionListItemModel): string | number {
	return item.isDefault
		? "default"
		: item.id;
}

export const topDeskConfigurationRouting = {
	page: "/TopDeskConfiguration"
};